import { useState, useEffect } from 'react'
import { Modal, Form, Button, Table, Spinner, Alert } from 'react-bootstrap'
import { t } from '../locale'
import { apiRequest } from '../helpers'
import { updateClient } from '../service/client'
import { Client } from '../react-app-env'

type SearchResult = {
  ekrn: string
  sitz: string
  firmenname: string
}

type Props = {
  show: boolean
  onClose: () => void
  client: Client | undefined
  clientName: string
  registernummer: string
  userEmail: string
  onSuccess?: () => void
}

export default function TransparenzregisterModal({
  show,
  onClose,
  client,
  clientName,
  userEmail,
  onSuccess,
}: Props) {
  const [firmenname, setFirmenname] = useState(clientName)
  const [registernummer, setRegisternummer] = useState('')
  const [postleitzahl, setPostleitzahl] = useState('')

  useEffect(() => {
    if (show) setFirmenname(clientName)

    setDocumentAvailable(client?.transparenzregister?.alreadyAvailable || false)
    setDocumentNotRequired(client?.transparenzregister?.notRequired || false)
  }, [
    show,
    clientName,
    client?.transparenzregister?.notRequired,
    client?.transparenzregister?.alreadyAvailable,
  ])
  const [trEmail, setTrEmail] = useState('')
  const [trPassword, setTrPassword] = useState('')

  const [searching, setSearching] = useState(false)
  const [results, setResults] = useState<SearchResult[]>([])
  const [selected, setSelected] = useState<SearchResult | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [searchError, setSearchError] = useState('')
  const [modalAlert, setModalAlert] = useState<{ variant: string; message: string } | null>(null)
  const [documentAvailable, setDocumentAvailable] = useState(
    client?.transparenzregister?.alreadyAvailable || false
  )
  const [documentNotRequired, setDocumentNotRequired] = useState(
    client?.transparenzregister?.notRequired || false
  )

  const resetSearch = () => {
    setResults([])
    setSelected(null)
    setSearchError('')
    setModalAlert(null)
  }

  const handleSearch = async () => {
    if (!firmenname.trim() && !registernummer.trim()) {
      setModalAlert({ variant: 'danger', message: t('trSearchRequiresFirmennameOrRegisternummer') })
      return
    }
    if (firmenname.trim().length < 2) {
      setModalAlert({ variant: 'danger', message: t('trFirmennameMinLength') })
      return
    }
    if (!trEmail.trim() || !trPassword.trim()) {
      setModalAlert({ variant: 'danger', message: t('trCredentialsRequired') })
      return
    }

    setSearching(true)
    resetSearch()

    try {
      const res = await apiRequest(
        `${process.env.REACT_APP_API_URL}/transparenzregister/search`,
        'POST',
        {
          firmenname: firmenname.trim() || null,
          registernummer: registernummer.trim() || null,
          postleitzahl: postleitzahl.trim() || null,
          trEmail: trEmail.trim(),
          trPassword,
        }
      )

      if (res?.status === 'error') {
        setSearchError(t(res.err === 'reauthRequired' ? 'sessionExpired' : 'trSearchFailed'))
        return
      }

      if (!res?.data?.length) {
        setSearchError(t('noResults'))
        return
      }

      setResults(res.data)
    } catch {
      setSearchError(t('trSearchFailed'))
    } finally {
      setSearching(false)
    }
  }

  const handleDocumentRequest = async () => {
    if (!selected) {
      return
    }

    setSubmitting(true)
    try {
      const res = await apiRequest(
        `${process.env.REACT_APP_API_URL}/transparenzregister/document-request`,
        'POST',
        {
          ekrn: selected.ekrn,
          sitz: selected.sitz,
          firmeName: selected.firmenname,
          userEmail,
          clientId: client?._id,
          registernummer: registernummer || null,
          trEmail: trEmail.trim(),
          trPassword,
        }
      )

      if (res?.message) {
        setModalAlert({ variant: res.message.variant, message: res.message.content })
      }

      if (res?.message?.variant === 'success') {
        onSuccess?.()
        onClose()
      }
    } catch {
      setModalAlert({ variant: 'danger', message: t('trDocumentRequestFailed') })
    } finally {
      setSubmitting(false)
    }
  }

  const markClientTransparenzregister = async (
    documentAvailable: boolean,
    documentNotRequired: boolean
  ) => {
    await updateClient({
      clientId: client?._id,
      transparenzregister: {
        alreadyAvailable: documentAvailable,
        notRequired: documentNotRequired,
      },
    })
    onClose()
  }

  const handleClose = () => {
    resetSearch()
    onClose()
  }

  return (
    <Modal show={show} onHide={handleClose} centered dialogClassName='modal-lg modern-modal'>
      <Modal.Header className='bg-gray-light' closeButton>
        <Modal.Title>{t('trModalTitle')}</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {results.length > 0 && (
          <div className='mb-4'>
            <h6 className='fw-semibold mb-2'>{t('trSearchResults')}</h6>
            <Table hover bordered size='sm' responsive>
              <thead className='table-light'>
                <tr>
                  <th></th>
                  <th>{t('trFirmenname')}</th>
                  <th>{t('trSitz')}</th>
                  <th>{t('trEkrn')}</th>
                </tr>
              </thead>
              <tbody>
                {results.map(item => (
                  <tr
                    key={item.ekrn}
                    onClick={() => setSelected(item)}
                    className={selected?.ekrn === item.ekrn ? 'table-primary' : ''}
                    style={{ cursor: 'pointer' }}
                  >
                    <td>
                      <Form.Check
                        type='radio'
                        checked={selected?.ekrn === item.ekrn}
                        onChange={() => setSelected(item)}
                        onClick={e => e.stopPropagation()}
                      />
                    </td>
                    <td>{item.firmenname}</td>
                    <td>{item.sitz}</td>
                    <td>{item.ekrn}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}

        {searchError && (
          <Alert variant='warning' className='mb-3'>
            {searchError}
          </Alert>
        )}

        {modalAlert && (
          <Alert
            variant={modalAlert.variant}
            className='mb-3'
            dismissible
            onClose={() => setModalAlert(null)}
          >
            {modalAlert.message}
          </Alert>
        )}

        <Form>
          <Form.Group className='mb-3'>
            <Form.Label>{t('trClientName')}</Form.Label>
            <Form.Control
              type='text'
              value={firmenname}
              onChange={e => {
                setFirmenname(e.target.value)
                resetSearch()
              }}
              autoComplete='off'
            />
          </Form.Group>

          <Form.Group className='mb-3'>
            <Form.Label>{t('trRegisternummer')}</Form.Label>
            <Form.Control
              type='text'
              value={registernummer}
              onChange={e => {
                setRegisternummer(e.target.value)
                resetSearch()
              }}
              autoComplete='off'
            />
          </Form.Group>

          <Form.Group className='mb-3'>
            <Form.Label>{t('trPostleitzahl')}</Form.Label>
            <Form.Control
              type='text'
              value={postleitzahl}
              onChange={e => {
                setPostleitzahl(e.target.value)
                resetSearch()
              }}
              autoComplete='off'
              placeholder={t('trPostleitzahlOptional')}
            />
          </Form.Group>

          <Form.Group className='mb-3'>
            <Form.Label>{t('trEmail')}</Form.Label>
            <Form.Control
              type='email'
              value={trEmail}
              onChange={e => setTrEmail(e.target.value)}
              autoComplete='off'
            />
          </Form.Group>

          <Form.Group className='mb-3'>
            <Form.Label>{t('trPassword')}</Form.Label>
            <Form.Control
              type='password'
              value={trPassword}
              onChange={e => setTrPassword(e.target.value)}
              autoComplete='new-password'
            />
          </Form.Group>

          <Button variant='success' onClick={handleSearch} disabled={searching || submitting}>
            {searching ? (
              <>
                <Spinner size='sm' className='me-1' />
                {t('trSearching')}
              </>
            ) : (
              t('trSearch')
            )}
          </Button>
        </Form>
      </Modal.Body>

      <Modal.Footer className='flex-column align-items-stretch gap-2'>
        <div className='d-flex flex-column gap-2 mb-1'>
          <Form.Check
            type='checkbox'
            id='tr-document-vorhanden'
            label={t('trDocumentVorhanden')}
            checked={documentAvailable}
            onChange={e => {
              setDocumentAvailable(e.target.checked)
            }}
            disabled={submitting || searching}
          />
          <Form.Check
            type='checkbox'
            id='tr-document-nicht-erforderlich'
            label={t('trDocumentNichtErforderlich')}
            checked={documentNotRequired}
            onChange={e => {
              setDocumentNotRequired(e.target.checked)
            }}
            disabled={submitting || searching}
          />
          <div>
            <Button
              variant='primary'
              disabled={submitting || searching}
              onClick={() => markClientTransparenzregister(documentAvailable, documentNotRequired)}
            >
              {t('update')}
            </Button>
          </div>
        </div>
        <div className='d-flex justify-content-end gap-2'>
          <Button variant='secondary' onClick={handleClose} disabled={searching || submitting}>
            {t('cancel')}
          </Button>

          <Button
            variant='primary'
            onClick={handleDocumentRequest}
            disabled={!selected || submitting || searching}
          >
            {submitting ? (
              <>
                <Spinner size='sm' className='me-1' />
                {t('trRequesting')}
              </>
            ) : (
              t('trDocumentRequest')
            )}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  )
}
